import { subscribeLiveblocksAvailability, useOthers, useUpdateMyPresence } from "@configs/liveblocks.config"
import { useCallback, useEffect, useMemo, useState } from "react"

interface UseEditingLockReturn {
    isLocked: boolean
    lockingUser: AvatarUser | null
    startEditing: () => void
    stopEditing: () => void
    isLiveblocksAvailable: boolean
}

/**
 * Hook for collaborative editing lock using Liveblocks presence.
 *
 * When a user starts editing, their presence is updated with the formId.
 * Other users see `isLocked: true` and the `lockingUser` info.
 * When liveblocks is unavailable, `isLocked` is always `false` (graceful degradation).
 *
 * @param formId - Unique identifier for the form/entity being edited (e.g., "job-series-abc123")
 *
 * @example
 * const { isLocked, lockingUser, startEditing, stopEditing } = useEditingLock(`job-series-${jobSeriesId}`)
 *
 * // When opening edit dialog
 * startEditing()
 *
 * // When closing edit dialog
 * stopEditing()
 *
 * // Check if another user is editing
 * if (isLocked) {
 *   // Show lockingUser avatar with tooltip
 * }
 */
export default function useEditingLock(formId: string): UseEditingLockReturn {
    const [isLiveblocksAvailable, setIsLiveblocksAvailable] = useState(true)

    const others = useOthers()
    const updateMyPresence = useUpdateMyPresence()

    // Subscribe to liveblocks availability
    useEffect(() => {
        const unsubscribe = subscribeLiveblocksAvailability((available) => {
            setIsLiveblocksAvailable(available)
        })

        return () => {
            unsubscribe()
        }
    }, [])

    // Find if any other user is editing this specific form
    // Memoize to prevent unnecessary re-renders
    const editingOther = useMemo(
        () => others.find((other) => other.presence?.editingFormId === formId),
        [others, formId],
    )

    const isLocked = useMemo(() => isLiveblocksAvailable && !!editingOther, [isLiveblocksAvailable, editingOther])

    const lockingUser = useMemo(() => editingOther?.info ?? null, [editingOther])

    const startEditing = useCallback(() => {
        updateMyPresence({ editingFormId: formId })
    }, [formId, updateMyPresence])

    const stopEditing = useCallback(() => {
        updateMyPresence({ editingFormId: null })
    }, [updateMyPresence])

    // Clean up editing state on unmount
    useEffect(() => {
        return () => {
            // Only clear if we're still in the same form context
            updateMyPresence({ editingFormId: null })
        }
    }, [updateMyPresence])

    return {
        isLocked,
        lockingUser,
        startEditing,
        stopEditing,
        isLiveblocksAvailable,
    }
}
